import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqs, testimonials } from "@/mocks/events";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da28uttv7gurs51ef9a0";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=dark%20documentary%20newsroom%20table%20with%20video%20monitors%20showing%20urban%20street%20scenes%2C%20warm%20orange%20desk%20lamps%2C%20camera%20equipment%20and%20notebooks%2C%20moody%20cinematic%20lighting%2C%20high%20contrast%2C%20gritty%20realistic%20photography&width=1600&height=900&seq=about-chigua-2026&orientation=landscape";

const POSITION = [
  { icon: "ri-crosshair-line", title: "内容定位", text: "专注热点事件现场实拍与事件合集，聚合全网第一手路人记录，按主题分类整理。" },
  { icon: "ri-group-line", title: "目标人群", text: "关心时事、喜欢围观真实现场、希望理性了解事件全貌的普通网友与内容爱好者。" },
  { icon: "ri-sparkling-line", title: "价值主张", text: "用无剪辑的原始画面还原真相，用完整的语境替代断章取义，让吃瓜更有分寸。" },
];

const PRINCIPLES = [
  "现场优先：优先呈现第一现场的路人实拍，保留时间、地点等关键背景信息。",
  "完整呈现：尽量还原事件完整经过与现场对话，避免单一片段误导判断。",
  "客观中立：只做信息整理与脉络梳理，不对当事人下结论、贴标签。",
  "理性引导：倡导就事论事、理性吃瓜，不参与网络暴力与人身攻击。",
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [fields, setFields] = useState({ name: "", email: "", subject: "", message: "" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot
    const honey = ((fd.get("contact_alt") as string) || "").trim();
    if (honey) {
      setStatus("success");
      setFields({ name: "", email: "", subject: "", message: "" });
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((v, k) => {
      if (k === "contact_alt") return;
      params.append(k, v.toString());
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (!res.ok) {
        setFormError(String(serverMsg || "提交失败，请稍后重试"));
        setStatus("error");
        return;
      }
      if (serverMsg && serverMsg.toLowerCase().includes("spam")) {
        setFormError(String(serverMsg));
        setStatus("error");
        return;
      }
      if (parsed?.code === "OK" || (!parsed && res.ok)) {
        setStatus("success");
        setFields({ name: "", email: "", subject: "", message: "" });
        form.reset();
      } else {
        setFormError(String(serverMsg || "提交失败，请稍后重试"));
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后重试");
      setStatus("error");
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于吃瓜视频在线",
      url: "https://zsfalv.cn/about",
      inLanguage: "zh-CN",
      description: "吃瓜视频在线聚合全网热点事件现场实拍，事件合集按主题分类，路人视角真实记录。",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://zsfalv.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://zsfalv.cn/about" },
      ],
    },
  ];

  const set = (k: keyof typeof fields, v: string) => setFields((p) => ({ ...p, [k]: v }));

  return (
    <>
      <Seo
        title="关于我们 - 吃瓜视频在线"
        description="了解吃瓜视频在线的品牌介绍、网站定位、发展理念、内容原则与用户口碑。聚合全网热点事件现场实拍，理性吃瓜，全程无剪辑还原事件全貌。"
        keywords="吃瓜视频在线,关于我们,现场实拍,内容原则"
        path="/about"
        jsonLd={jsonLd}
      />

      {/* hero */}
      <section className="relative flex min-h-[380px] w-full items-center overflow-hidden">
        <img src={HERO_IMG} alt="吃瓜视频在线新闻工作台背景" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40"></div>
        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="transition hover:text-primary-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-300">关于我们</span>
          </nav>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-950/50 px-4 py-1.5 text-xs font-medium text-primary-400">
            <i className="ri-quill-pen-line"></i>关于我们
          </span>
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-5xl">关于吃瓜视频在线</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-400 md:text-base">
            一个专注于热点事件现场实拍与事件合集的平台。我们用路人视角的真实记录，帮你第一时间掌握事件全貌。
          </p>
        </div>
      </section>

      {/* 品牌介绍 + 网站定位 */}
      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">品牌介绍</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">把第一现场，带回给你</h2>
            <p className="mt-4 text-sm leading-8 text-foreground-600">
              吃瓜视频在线诞生于一个朴素的观察：当热点事件发生时，网上流传的多是经过剪辑、带立场的片段，真相往往藏在被删掉的那几秒里。于是我们专注于聚合全网的第一手现场实拍，把镜头交给站在事件现场的路人。
            </p>
            <p className="mt-3 text-sm leading-8 text-foreground-600">
              我们按主题把散落的事件归拢成合集，从现场实拍、事件合集、路人视角，到对话录音与全程记录，力求每一段内容都保留完整的语境。在这里，你可以放下成见，先把事件看完，再做判断。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {POSITION.map((p) => (
              <div key={p.title} className="flex flex-col rounded-lg border border-background-200/60 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 发展理念 */}
      <section id="values" className="border-y border-background-200/60 bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">发展理念</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">从真实出发，向理性生长</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              我们不追求流量最大化，只在乎信息是否经得起推敲。平台每一步的成长，都围绕下面几个原则展开。
            </p>
          </div>

          <div className="mt-10 space-y-6">
            {[
              { step: "01", title: "看见真实", text: "从路人视角出发，聚合现场实拍，保留原始画面与对话，让事件以最朴素的样子被看见。" },
              { step: "02", title: "还原完整", text: "把碎片拼成脉络，按主题归拢事件合集，补充时间、地点与背景，帮助用户看清来龙去脉。" },
              { step: "03", title: "保持克制", text: "只做客观的信息整理，不渲染对立、不煽动情绪，为理性讨论留出空间。" },
              { step: "04", title: "持续进化", text: "不断完善分类与搜索体验，让每一件值得关注的事件，都能被需要的人快速找到。" },
            ].map((s) => (
              <div key={s.step} className="flex gap-5 rounded-lg border border-background-200/60 bg-background-50 p-5 md:p-6">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 font-heading text-base font-black text-background-50">
                  {s.step}
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-accent-500">内容原则</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们如何筛选与整理内容</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              面对海量的热点现场，我们有一套不妥协的准则，确保呈现在你面前的内容真实、完整、克制。
            </p>
            <ul className="mt-6 space-y-3">
              {PRINCIPLES.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-lg border border-background-200/60 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-sm"></i>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {/* 用户口碑 */}
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">用户口碑</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">来自围观者的真实声音</h2>
            <div className="mt-6 space-y-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                  <div className="flex gap-0.5 text-secondary-500">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-800">
                      {t.name.charAt(0)}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-foreground-900">{t.name}</span>
                      <span className="block text-xs text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 联系方式 */}
      <section className="border-y border-background-200/60 bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <span className="font-label text-xs font-semibold tracking-widest text-primary-500">联系我们</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">留下你的看法与建议</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                无论是事件线索、内容建议，还是对我们的批评，都欢迎告诉我们。你的每一条反馈，都是我们改进的动力。
              </p>
              <div className="mt-6 space-y-4">
                {[
                  { icon: "ri-mail-line", label: "邮箱", value: "contact@chigua-video.example" },
                  { icon: "ri-time-line", label: "更新时段", value: "每日 09:00 – 22:00" },
                  { icon: "ri-question-answer-line", label: "反馈渠道", value: "通过右侧表单提交，工作日及时回复" },
                ].map((c) => (
                  <div key={c.label} className="flex items-center gap-4 rounded-lg border border-background-200/60 bg-background-50 p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="吃瓜视频在线意见反馈表单" className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-lg font-bold text-foreground-950">意见反馈</h3>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-600">称呼</label>
                  <input id="fb-name" name="name" type="text" value={fields.name} onChange={(e) => set("name", e.target.value)} placeholder="如何称呼你" className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-500 focus:border-primary-500" />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-600">邮箱</label>
                  <input id="fb-email" name="email" type="email" value={fields.email} onChange={(e) => set("email", e.target.value)} placeholder="用于接收回复" className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-500 focus:border-primary-500" />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="fb-subject" className="mb-1.5 block text-xs font-medium text-foreground-600">反馈主题</label>
                <input id="fb-subject" name="subject" type="text" value={fields.subject} onChange={(e) => set("subject", e.target.value)} placeholder="例如：内容建议、事件线索、问题反馈" className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-500 focus:border-primary-500" />
              </div>
              <div className="mt-4">
                <label htmlFor="fb-message" className="mb-1.5 block text-xs font-medium text-foreground-600">反馈内容</label>
                <textarea id="fb-message" name="message" value={fields.message} onChange={(e) => set("message", e.target.value)} rows={5} maxLength={500} placeholder="请尽量描述清楚你的建议或线索（500字以内）" className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-500 focus:border-primary-500"></textarea>
                <p className="mt-1 text-right text-xs text-foreground-500">{fields.message.length}/500</p>
              </div>
              <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-guard" />

              {status === "success" && (
                <p className="mt-4 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mr-1.5"></i>感谢你的反馈，我们已收到，会尽快查看处理！
                </p>
              )}
              {status === "error" && formError && (
                <p className="mt-4 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-error-warning-line mr-1.5"></i>{formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? "提交中…" : "提交反馈"} <i className="ri-send-plane-line"></i>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">常见问题</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">你可能还想了解</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">关于内容来源、真实性保障与理性吃瓜准则的更多说明。</p>
          </div>
          <div className="lg:col-span-2">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>
    </>
  );
}